import clsx from 'clsx'
import { Maybe } from 'graphql/jsutils/Maybe'
import React, { useState } from 'react'
import { Control, Controller, UseFormSetValue } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import s from './styles.module.scss'

import ProposalClientRequestCreateProjectModal from './proposal-client-request-create-project-modal/ProposalClientRequestCreateProjectModal'
import {
  ISelectProposalForm,
  ISelectProposalHook,
} from '../modal-select-proposal.types'
import {
  HousePictureContext,
  HousesInformationsFieldsFragment,
} from '../../../../lib/graphql'
import { Button, BUTTON_COLOR } from '../../../atoms'
import Icon from '../../../atoms/icons/icon'

interface IProjectCardProps {
  control: Control<ISelectProposalForm, any>
  clientRequestId?: string
  briefTitle?: Maybe<string>
  mainPictures?: Maybe<Omit<HousePictureContext, 'position'>[]>
  clientProposalsCount?: Maybe<number>
}

function NewProjectRow({ onClick }: { onClick: () => void }) {
  return (
    <Button
      color={BUTTON_COLOR.SECONDARY}
      className={s.menuItem}
      onClick={onClick}
      data-testid='new-project-button'
    >
      <div className={s.newProjetButtonContent}>
        <Icon name='add' size={24} />
        <FormattedMessage
          id='ui.ModalSelectProposalClientRequest.createNewProject'
          defaultMessage='Créer un nouveau projet'
        />
      </div>
    </Button>
  )
}

const ProjectCard = ({
  control,
  clientRequestId,
  briefTitle,
  mainPictures,
  clientProposalsCount = 1,
}: IProjectCardProps) => {
  // ensure we have 4 pictures - if not, we add null values
  const pictures = Array.from(
    { length: 4 },
    (_, i) => mainPictures?.[i] ?? null,
  )

  return (
    <Controller
      control={control}
      name='clientRequestId'
      render={({ field }) => {
        const isSelected = field.value === clientRequestId
        return (
          <Button
            color={BUTTON_COLOR.SECONDARY}
            className={clsx(s.menuItem, {
              [s.selected]: isSelected,
            })}
            contentClassName={s.clientRequestItem}
            key={clientRequestId}
            onClick={() => {
              field.onChange(clientRequestId)
            }}
            dataMixpanel={{
              name: 'select-proposal-client-request',
              data: { clientRequestId },
            }}
          >
            {pictures &&
              pictures.map((picture, index) => {
                return (
                  <div
                    key={picture?._id ?? `empty-${index}`}
                    className={s.imageContainer}
                    style={{ gridArea: `image${index}` }}
                  >
                    {picture && (
                      <img
                        className={s.coverImage}
                        key={clientRequestId}
                        src={picture.uri}
                        alt='house-cover-image'
                        draggable={false}
                      />
                    )}
                  </div>
                )
              })}
            <div className={s.title}>
              <FormattedMessage
                id='ui.ProjectCard.projectTitle'
                defaultMessage='Projet : {briefTitle}'
                values={{ briefTitle: briefTitle ?? 'Projet sans nom' }}
              />
            </div>
            <div className={s.subtitle}>
              <FormattedMessage
                id='ui.ProjectCard.subtitle'
                defaultMessage='{proposalNumber, plural, =0 {Aucun lieu enregistré} one {1 lieu enregistré} other {# lieux enregistrés}}'
                values={{ proposalNumber: clientProposalsCount ?? 0 }}
              />
            </div>
            <div className={s.selectionState}>
              <div className={s.selectionIncentive}>
                <FormattedMessage
                  id='ui.ProjectCard.addToProject'
                  defaultMessage='Ajouter à ce projet'
                />
              </div>
              <Icon
                className={isSelected ? s.radioChecked : s.radioUnchecked}
                size={24}
                name={isSelected ? 'radio-checked' : 'radio-unchecked'}
              />
            </div>
          </Button>
        )
      }}
    />
  )
}

interface IModalSelectProposalClientRequestProps {
  control: Control<ISelectProposalForm, any>
  house: HousesInformationsFieldsFragment
  briefTitle: string | undefined
  setValue: UseFormSetValue<ISelectProposalForm>
  clientRequests: ISelectProposalHook['clientRequests']
  isLowRes?: boolean
}

const ModalSelectProposalClientRequest = ({
  control,
  house,
  briefTitle,
  setValue,
  clientRequests,
  isLowRes = false,
}: IModalSelectProposalClientRequestProps) => {
  const [isNewCardVisible, setIsNewCardVisible] = useState(false)

  const [isCreateProjectModalOpen, setCreateProjectModalOpen] = useState(false)

  const onCreateNewProject = () => {
    setIsNewCardVisible(true)
    setCreateProjectModalOpen(false)
    setValue('clientRequestId', undefined)
  }

  return (
    <div className={s.container} style={{ padding: isLowRes ? '0' : '' }}>
      <div className={s.content}>
        {!isLowRes && (
          <div className={s.listHeader}>
            <FormattedMessage
              id='ui.ModalSelectProposalClientRequest.addQuoteToProject'
              defaultMessage='Ajouter le devis à un projet'
            />
          </div>
        )}
        {clientRequests?.slice(0, 2).map((clientRequest) => {
          return (
            <ProjectCard
              key={clientRequest._id}
              control={control}
              clientRequestId={clientRequest._id}
              briefTitle={clientRequest.briefTitle}
              mainPictures={clientRequest.mainPictures}
              clientProposalsCount={clientRequest.clientProposalsCount}
            />
          )
        })}

        {isNewCardVisible ? (
          <ProjectCard
            control={control}
            clientRequestId={undefined}
            briefTitle={briefTitle}
            mainPictures={[house.coverPicture]}
          />
        ) : (
          <NewProjectRow onClick={() => setCreateProjectModalOpen(true)} />
        )}
        <ProposalClientRequestCreateProjectModal
          isOpen={isCreateProjectModalOpen}
          onRequestClose={() => setCreateProjectModalOpen(false)}
          className={s.createProjectModal}
          control={control}
          onClick={onCreateNewProject}
        />
      </div>
    </div>
  )
}

export default ModalSelectProposalClientRequest
